//@author: devin

import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import {
    ApplicationRef,
    ComponentRef,
    createComponent,
    EmbeddedViewRef,
    inject,
    Injectable,
    Injector,
    NgZone,
    Type,
} from "@angular/core";
import { ServiceLoader } from "../../services/service.loader";
import { S25Util } from "../../util/s25-util";
import { LangService } from "../../services/lang.service";
import { HelpTopic } from "../s25-help/s25.help.service";
import { ModalComponent } from "./modal.component";
import { ModalDataTypes, ModalDefinition, ModalMap, ModalName } from "./modal.map";
import { jSith } from "../../util/jquery-replacement";
import { ModalConfig, S25ModalComponent } from "../s25-modal/s25.modal.component";
import { ModalDialogParams } from "./modals/modal.dialog.component";
import { AriaLive } from "../../services/aria.live.service";

const CLICK_OUTSIDE_REJECTION = 0;
const ESCAPE_KEY_REJECTION = 1;

export interface ModalData {
    lang: any;
    isUnsupportedBrowser: boolean;

    closeModal(result?: any): void;

    callBack(): void;

    modalInstance: NgbModalRef;
    title?: string;
    iconClass?: string;
    helpLink?: HelpTopic;
    size?: "sm" | "md" | "lg" | "xl" | "xxl";
    windowClass?: string;
    closable?: boolean;
    injector?: Injector;
}

@Injectable({
    providedIn: "root",
})
export class ModalService {
    private static zone?: NgZone = undefined;
    private static ngbModal?: NgbModal = undefined;
    private static returnToFocusElement: any = null;
    private static queue: Promise<unknown>[] = [];
    private static openModals: Modal[] = [];

    appRef = inject(ApplicationRef);

    private static registerModal(modal: Modal) {
        this.openModals.push(modal);
    }

    private static unregisterModal(modal: Modal) {
        this.openModals = this.openModals.filter((p) => p !== modal);
    }

    public static modal<K extends ModalName>(
        modalType: K,
        data?: ModalDataTypes[K] & Partial<ModalData> & Partial<Omit<ModalDefinition, "controller">>,
    ) {
        data ??= <any>{};
        data!.windowClass = modalType;
        // TODO: stop manipulating the passed-in object. Return a value instead, e.g.
        //  ```ts
        //  return await ModalService.byClass(
        //    ModalMap.typedMap[modalType].controller,
        //    { ...data, ...ModalMap.typedMap[modalType], windowClass: modalType }
        //  ```
        Object.entries(ModalMap.typedMap[modalType]).forEach(([key, value]) => {
            if (!(key in data!)) {
                (<any>data)[key] = value;
            }
        });
        // TODO: figure out this type :(
        return ModalService.byClass(<any>ModalMap.typedMap[modalType].controller, <any>data);
    }

    public static byClass<M extends Type<ModalComponent<Params>>, Params = ModalComponent.Params<InstanceType<M>>>(
        componentType: M & Partial<Omit<ModalDefinition, "controller">>,
        data?: Omit<InstanceType<M>["data"], keyof ModalData> &
            Partial<ModalData> &
            Partial<Omit<ModalDefinition, "controller">>,
    ) {
        ModalService.zone = ModalService.zone || ServiceLoader.get(NgZone);
        ModalService.ngbModal = ModalService.ngbModal || ServiceLoader.get(NgbModal);
        ModalService.returnToFocusElement = document.activeElement;
        const { promise, resolve, reject } = S25Util.createPromise<void>();
        let opened = false;
        return ModalService.zone!.run(() => {
            return LangService.getLang().then((lang) => {
                const modalRef: NgbModalRef = ModalService.ngbModal!.open(componentType, {
                    // CSS/Angular exit animations are not flushed by Karma $timeout helpers
                    animation: !S25Util.isInIframe && !window.unitTesting,
                    keyboard: data?.closable ?? true,
                    size: data?.size || componentType.size || "lg",
                    backdrop: (data?.asDialog ?? componentType.asDialog) ? "static" : true,
                    windowClass: data?.windowClass ?? componentType.windowClass ?? "generic-modal",
                    injector: data?.injector,
                });
                ModalService.registerModal({ close: modalRef.close, closed: promise });
                opened = true;

                const componentInstance: InstanceType<M> = modalRef.componentInstance;
                if (modalRef.componentInstance) {
                    data ??= <any>{};
                    // TODO: stop modifying the passed-in object
                    data!.title ??= componentType.title;
                    data!.iconClass ??= componentType.iconClass;
                    data!.lang = lang;
                    data!.isUnsupportedBrowser = S25Util.isUnsupportedBrowser();
                    data!.closeModal = (result?: any) => modalRef.close(result);
                    data!.modalInstance = modalRef;
                    data!.closable ??= true;
                    componentInstance.data = data as Params & ModalData;
                    componentInstance.cd.markForCheck();
                }

                // ngb modal automatically focuses the first focusable element in the modal
                // but this causes a problem because, in an iframe, it takes a bit for the modal to
                // position itself, so if the modal is opened and focused immediately, the focus will
                // cause the page to scroll to the top. So we override and eliminate the autofocus behavior
                // by placing the ngbAutofocus attribute on the modal header, which is not focusable.
                // Then we wait 1 second for the modal to be positioned properly and focus the first
                // focusable element in the modal ourselves
                S25Util.isInIframe &&
                    setTimeout(() => {
                        const focusableElem = opened && jSith.focusable("ngb-modal-window", 0, false, false);
                        focusableElem && focusableElem.focus();
                    }, 1000);

                return modalRef.result
                    .catch((rejection) => {
                        // Swallow NgbModal dismiss rejections
                        if (rejection === CLICK_OUTSIDE_REJECTION || rejection === ESCAPE_KEY_REJECTION || !rejection) {
                            return;
                        }
                        throw rejection;
                    })
                    .finally(() => {
                        opened = false;
                        // Pull any live regions out before destroying the modal
                        AriaLive.restoreRegionsToBody();
                        ModalService.returnToFocusElement && ModalService.returnToFocusElement.focus();
                        resolve();
                        ModalService.unregisterModal({ close: modalRef.close, closed: promise });
                    });
            });
        });
    }

    public static async queueModal(modalType: ModalName, data?: ModalDataTypes[ModalName] & Partial<ModalData>) {
        await Promise.allSettled(this.openModals.map((modal) => modal.closed)); // Wait for any open modal not in queue
        const q = this.queue.slice(); // Copy queue
        const { promise, resolve, reject } = S25Util.createPromise();
        this.queue.push(promise);
        await Promise.allSettled(q); // Wait for other modals
        const ret = await this.modal(modalType, data);
        this.queue.shift(); // Pop own promise
        resolve();
        return ret;
    }

    public static closeAll() {
        ModalService.ngbModal?.dismissAll();
        // Closing the modal will automatically unregister it via promise.finally()
        for (const modal of ModalService.openModals) modal.close();
    }

    public static async dialog({
        type,
        title,
        message,
        primary,
    }: {
        type: Parameters<typeof ModalService.dialogType>[0];
        title: string;
        message: string;
        primary: Parameters<typeof ModalService.dialogType>[2];
    }) {
        const dialogData = ModalService.dialogType(type, { message, title }, primary);
        await ModalService.modal("dialog", dialogData);
        return dialogData.answer === ModalDialogParams.Answer.Yes;
    }

    public static dialogType = function (
        type: ModalDialogParams.ButtonType | `${ModalDialogParams.ButtonType} ${ModalDialogParams.ButtonType}`,
        data: Readonly<Pick<ModalData, "title"> & Pick<ModalDialogParams, "message">>,
        primary?: ModalDialogParams["buttonMap"]["primary"],
    ): ModalDialogParams & Partial<ModalData> {
        const options = type.split(" ");
        const buttonMap: ModalDialogParams["buttonMap"] = {};
        jSith.forEach(options, function (_, val) {
            switch (val) {
                case "Yes":
                    buttonMap.hasYes = 1;
                    break;
                case "No":
                    buttonMap.hasNo = 1;
                    break;
                case "Cancel":
                    buttonMap.hasCancel = 1;
                    break;
                case "Continue":
                    buttonMap.hasContinue = 1;
                    break;
                case "OK":
                    buttonMap.hasOK = 1;
                    break;
                default:
                    break;
            }
        });
        buttonMap.primary = primary;
        return {
            ...data,
            answer: ModalDialogParams.Answer.No,
            buttonMap: buttonMap,
        };
    };

    s25Modal<CloseData extends any = any, TemplateContext extends object = any>(
        config: ModalConfig<CloseData, TemplateContext>,
    ): Modal<CloseData> {
        const componentRef = createComponent(S25ModalComponent<CloseData, TemplateContext>, {
            environmentInjector: this.appRef.injector,
        });
        const componentElem: HTMLElement = (<EmbeddedViewRef<unknown>>componentRef.hostView).rootNodes[0];
        const closedPromise = S25Util.createPromise<CloseData>();
        const close = (data?: CloseData) => {
            document.body.removeChild(componentRef.location.nativeElement);
            this.appRef.detachView(componentRef.hostView);
            componentRef.destroy();
            closedPromise.resolve(data);
        };
        document.body.append(componentElem);
        this.appRef.attachView(componentRef.hostView);

        const modalPromise = componentRef.instance.open(config);
        modalPromise.then(closedPromise.resolve, closedPromise.reject);

        const modal: Modal<CloseData> = { close, closed: closedPromise.promise };
        ModalService.registerModal(modal);
        closedPromise.promise.finally(() => ModalService.unregisterModal(modal));
        return modal;
    }
}

type Modal<CloseData = unknown> = { close: (data?: CloseData) => unknown; closed: Promise<CloseData> };
